import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Trophy, MapPin, Calendar, ChevronRight, ArrowLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { API } from '@/App';
import { format, parseISO } from 'date-fns';

export default function LeaguesPage({ user, setUser, showTeams = false }) {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        const response = await axios.get(`${API}/leagues`, {
          params: search ? { search } : {}
        });
        setLeagues(response.data);
      } catch (error) {
        console.error('Error fetching leagues:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchLeagues, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 main-content-mobile">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-3 h-14">
            <Link to="/">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="font-bold text-lg">Leagues</h1>
          </div>
        </div>
      </header>

      {/* Search */}
      <div className="pt-14 px-4 py-4 bg-blue-600">
        <div className="max-w-7xl mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search leagues..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 h-12 rounded-xl bg-white border-0"
              data-testid="league-search-input"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="skeleton h-40 rounded-2xl"></div>
              ))}
            </div>
          ) : leagues.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {leagues.map((league) => (
                <Link key={league.league_id} to={`/leagues/${league.league_id}`}>
                  <Card className="card-base cursor-pointer h-full" data-testid={`league-card-${league.league_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {league.logo_url ? (
                          <img src={league.logo_url} alt={league.name} className="w-14 h-14 rounded-xl object-cover" />
                        ) : (
                          <div className="w-14 h-14 rounded-xl team-logo-placeholder text-xl">
                            {league.name.charAt(0)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-lg text-slate-900 truncate">{league.name}</h3>
                          <Badge className="mt-1 bg-blue-100 text-blue-700 border-none">{league.season}</Badge>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      </div>
                      
                      <div className="mt-4 space-y-2 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          {league.location}
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {formatDate(league.start_date)} - {formatDate(league.end_date)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="empty-state bg-white rounded-2xl">
              <div className="empty-state-icon">
                <Trophy className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-semibold text-slate-900">No leagues found</h3>
              <p className="text-slate-500 text-sm mt-1">
                {search ? 'Try a different search term' : 'No leagues have been created yet'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
